import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRememberedState, useRememberedScroll } from '../../hooks/useRemembered';
import {
    AlertTriangle, CheckCircle2, ChevronRight, ClipboardCheck, HardHat, Loader2,
    MapPin, Plus, Receipt, RefreshCw, Search, Target, Users, X,
} from 'lucide-react';
import { Button } from '../Button';
import { CustomerRatingStars } from '../crm/CustomerRatingStars';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import {
    Freelancer,
    FreelancerQualification,
    STATUS_LABELS,
    einsatzbereitschaft,
    formatAnschrift,
    formatCents,
    heuteIso,
} from '../../services/freelancerLogic';
import {
    createFreelancer,
    fetchAllQualifications,
    fetchFreelancers,
} from '../../services/freelancerService';
import { fetchReviewQueue } from '../../services/assignmentService';
import { offenePruefungenZahl } from '../../services/freelancerInspectionLogic';
import { InvoiceConfig } from '../../types';
import { CreditNotesView } from './CreditNotesView';
import { FreelancerDetail } from './FreelancerDetail';
import { FreelancerMatchPanel } from './FreelancerMatchPanel';
import { ReviewQueue } from './ReviewQueue';

// Container der Freelancer-Verwaltung (ERP/CRM-Plan, Abschnitt 7 — Phase D1).
//
// Duenner Container (Standing Decision): laedt Stammdaten und Nachweise EINMAL
// (zwei Abfragen, kein N+1 je Zeile), haelt die Auswahl und reicht alles
// Weitere an FreelancerDetail bzw. FreelancerMatchPanel durch. Jede fachliche
// Aussage — einsatzbereit oder blockiert, und warum — kommt aus
// freelancerLogic.ts.
//
// Der Reiter „Pruefung" (D4) ist die Vier-Augen-Pruefung: alle eingereichten
// Begehungsberichte, mandantenweit. Er laedt seine Liste selbst; hier steht nur
// die ZAHL fuer das Abzeichen, damit sie auch dann sichtbar ist, wenn der
// Reiter gar nicht offen ist.
//
// Der Reiter „Gutschriften" (D5) ist die Abrechnung: Monatslauf, Belegliste,
// Ausstellen/Storno/PDF/DATEV. Er laedt seine Daten selbst und bekommt von hier
// nur den Freelancer-Stamm (fuer Namen und Steuersatz) und die PDF-/DATEV-
// Konfiguration durchgereicht.
//
// Kein Freelancer-Login: diese Ansicht ist reine Innendienst-Sicht — Admins und
// wer das Recht „Freelancer verwalten" hat (Wache in App.tsx; die Datenbank
// prueft dasselbe ab Nr. 153).

const STATUS_KLASSEN: Record<Freelancer['status'], string> = {
    aktiv: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pausiert: 'bg-amber-50 text-amber-700 border-amber-200',
    gesperrt: 'bg-slate-100 text-slate-500 border-slate-200',
};

interface Props {
    /** Sprung in die Projektansicht aus der Pruefliste. Optional — ohne ihn
     *  zeigt die Liste nur den Projektnamen. */
    onOpenProject?: (projectId: string) => void;
    /**
     * Firmenstammdaten, Rechnungs- und Briefkopf-Konfiguration fuer die
     * Gutschriften (D5). Optional: ohne sie bleibt der Reiter benutzbar, das
     * PDF traegt dann nur einen kargen Briefkopf.
     */
    company?: { name?: string; address?: string; logo?: string; footerText?: string; senderLine?: string };
    invoiceConfig?: InvoiceConfig;
    quoteConfig?: unknown;
    /** Name unter der Grussformel des Gutschrift-PDF. */
    userName?: string;
}

export const FreelancerView: React.FC<Props> = ({
    onOpenProject,
    company,
    invoiceConfig,
    quoteConfig,
    userName,
}) => {
    const [freelancers, setFreelancers] = useState<Freelancer[]>([]);
    const [qualis, setQualis] = useState<FreelancerQualification[]>([]);
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);

    const [suche, setSuche] = useRememberedState('freelancer.search', '');
    const scrollRef = useRememberedScroll('freelancer.scroll');
    const [bereich, setBereich] = useRememberedState<'stamm' | 'matching' | 'pruefung' | 'gutschriften'>('freelancer.bereich', 'stamm');
    const [offenePruefungen, setOffenePruefungen] = useState(0);
    const [ausgewaehltId, setAusgewaehltId] = useRememberedState<string | null>('freelancer.selected', null);

    const [anlegenOffen, setAnlegenOffen] = useState(false);
    const [neuerName, setNeuerName] = useState('');
    const [legtAn, setLegtAn] = useState(false);

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            // Nachweise werden fuer ALLE Zeilen auf einmal geholt: die Liste
            // zeigt je Zeile den Bereitschaftsstatus, und eine Abfrage je
            // Freelancer waere ein N+1 auf einer Seite, die ohnehin alles
            // anzeigt.
            const [stamm, nachweise] = await Promise.all([fetchFreelancers(), fetchAllQualifications()]);
            setFreelancers(stamm);
            setQualis(nachweise);

            // Das Abzeichen der Pruefliste. Bewusst SEPARAT und mit
            // verschlucktem Fehler: laeuft die Migration Nr. 21 noch nicht,
            // soll die Stammdatenansicht deswegen keinen Ladefehler zeigen.
            try {
                setOffenePruefungen(offenePruefungenZahl(await fetchReviewQueue()));
            } catch {
                setOffenePruefungen(0);
            }
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLaedt(false);
        }
    }, []);

    useEffect(() => {
        void laden();
    }, [laden]);

    const heute = heuteIso();

    /** Nachweise je Freelancer — einmal gruppiert statt je Zeile gefiltert. */
    const qualisNachId = useMemo(() => {
        const m = new Map<string, FreelancerQualification[]>();
        for (const q of qualis) {
            const liste = m.get(q.freelancerId);
            if (liste) liste.push(q);
            else m.set(q.freelancerId, [q]);
        }
        return m;
    }, [qualis]);

    const gefiltert = useMemo(() => {
        const s = suche.trim().toLowerCase();
        if (!s) return freelancers;
        return freelancers.filter((f) =>
            [f.name, f.firma, f.city, f.zip].filter(Boolean).join(' ').toLowerCase().includes(s),
        );
    }, [freelancers, suche]);

    const ausgewaehlt = useMemo(
        () => freelancers.find((f) => f.id === ausgewaehltId) ?? null,
        [freelancers, ausgewaehltId],
    );

    const einsatzbereiteAnzahl = useMemo(
        () => freelancers.filter((f) => einsatzbereitschaft(f, qualisNachId.get(f.id) ?? [], heute).bereit).length,
        [freelancers, qualisNachId, heute],
    );

    const uebernehmen = (aktualisiert: Freelancer) =>
        setFreelancers((prev) => prev.map((f) => (f.id === aktualisiert.id ? aktualisiert : f)));

    /** Nachweise EINES Freelancers ersetzen, ohne die uebrigen anzufassen. */
    const qualisSetzen = (freelancerId: string, neu: FreelancerQualification[]) =>
        setQualis((prev) => [...prev.filter((q) => q.freelancerId !== freelancerId), ...neu]);

    const anlegen = async () => {
        const name = neuerName.trim();
        if (!name) return;
        setLegtAn(true);
        setFehler(null);
        try {
            const neu = await createFreelancer({ name } as any);
            setFreelancers((prev) => [...prev, neu].sort((a, b) => a.name.localeCompare(b.name, 'de')));
            setAnlegenOffen(false);
            setNeuerName('');
            setAusgewaehltId(neu.id);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLegtAn(false);
        }
    };

    // ── Detailansicht ───────────────────────────────────────────────────────
    if (ausgewaehlt) {
        return (
            <FreelancerDetail
                freelancer={ausgewaehlt}
                qualis={qualisNachId.get(ausgewaehlt.id) ?? []}
                onChange={uebernehmen}
                onQualisChange={(neu) => qualisSetzen(ausgewaehlt.id, neu)}
                onBack={() => setAusgewaehltId(null)}
            />
        );
    }

    const bereichsWahl = (
        <div data-glass="umschalter" className="inline-flex rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            {([
                { key: 'stamm' as const, label: 'Stammdaten', icon: <Users className="w-4 h-4" />, badge: 0 },
                { key: 'matching' as const, label: 'Matching', icon: <Target className="w-4 h-4" />, badge: 0 },
                { key: 'pruefung' as const, label: 'Prüfung', icon: <ClipboardCheck className="w-4 h-4" />, badge: offenePruefungen },
                { key: 'gutschriften' as const, label: 'Gutschriften', icon: <Receipt className="w-4 h-4" />, badge: 0 },
            ]).map((b) => (
                <button
                    key={b.key}
                    type="button"
                    onClick={() => setBereich(b.key)}
                    aria-pressed={bereich === b.key}
                    className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all ${
                        bereich === b.key
                            ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                            : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                    }`}
                >
                    {b.icon}
                    {b.label}
                    {b.badge > 0 && (
                        <span className="bg-violet-600 text-white rounded-full px-1.5 py-0.5 text-[11px] leading-none">
                            {b.badge}
                        </span>
                    )}
                </button>
            ))}
        </div>
    );

    return (
        <div ref={scrollRef} data-glass-view="freelancer" className="h-full overflow-y-auto">
            <div className="space-y-6 max-w-7xl mx-auto animate-fade-in p-4 md:p-6 pb-20">
                {bereichsWahl}

                {/* ── Kopf ── */}
                <div data-glass="kopf" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col lg:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-4 w-full lg:w-auto">
                        <div className="p-3 bg-violet-50 text-violet-600 rounded-2xl">
                            <HardHat className="w-8 h-8" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-800">Freelancer</h2>
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">
                                {freelancers.length} erfasst · {einsatzbereiteAnzahl} einsatzbereit
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-3 w-full lg:w-auto justify-end">
                        <Button variant="secondary" onClick={laden} icon={<RefreshCw className={`w-4 h-4 ${laedt ? 'animate-spin' : ''}`} />}>
                            Neu laden
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => { setNeuerName(''); setAnlegenOffen(true); }}
                            className="shadow-lg border-b-2 border-blue-700"
                            icon={<Plus className="w-4 h-4" />}
                        >
                            Neuer Freelancer
                        </Button>
                    </div>
                </div>

                {fehler && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl p-4 text-sm flex items-start justify-between gap-3">
                        <span className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            {fehler}
                        </span>
                        <button onClick={() => setFehler(null)} className="shrink-0 hover:bg-rose-100 rounded p-1">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {bereich === 'gutschriften' ? (
                    <CreditNotesView
                        freelancers={freelancers}
                        company={company}
                        invoiceConfig={invoiceConfig}
                        quoteConfig={quoteConfig}
                        userName={userName}
                    />
                ) : bereich === 'pruefung' ? (
                    <ReviewQueue
                        freelancers={freelancers}
                        onOpenProject={onOpenProject}
                        onChanged={laden}
                    />
                ) : bereich === 'matching' ? (
                    <FreelancerMatchPanel
                        freelancers={freelancers}
                        qualis={qualis}
                        onOpenFreelancer={(f) => setAusgewaehltId(f.id)}
                    />
                ) : (
                    <>
                        {/* ── Suche ── */}
                        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                            <div className="relative w-full group">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                                <input
                                    type="text"
                                    placeholder="Suchen nach Name, Firma oder Ort…"
                                    value={suche}
                                    onChange={(e) => setSuche(e.target.value)}
                                    className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                />
                            </div>
                        </div>

                        {/* ── Liste ── */}
                        {laedt ? (
                            <div className="py-24 text-center text-slate-400 bg-white rounded-3xl border border-slate-200">
                                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
                                <p className="text-sm font-medium">Freelancer werden geladen…</p>
                            </div>
                        ) : gefiltert.length === 0 ? (
                            <div className="py-20 text-center text-slate-400 bg-white rounded-3xl border-2 border-dashed border-slate-100">
                                <div className="bg-slate-50 p-4 rounded-full w-fit mx-auto mb-4">
                                    <HardHat className="w-8 h-8 text-slate-300" />
                                </div>
                                <p className="font-bold text-slate-600">
                                    {freelancers.length === 0 ? 'Noch kein Freelancer erfasst' : 'Kein Treffer'}
                                </p>
                                <p className="text-xs mt-1">
                                    {freelancers.length === 0
                                        ? 'Über „Neuer Freelancer" den ersten Stammsatz anlegen.'
                                        : 'Suchbegriff ändern oder zurücksetzen.'}
                                </p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 bg-slate-50/70 border-b border-slate-100">
                                                <th className="px-4 py-3 font-bold">Name</th>
                                                <th className="px-4 py-3 font-bold">Ort</th>
                                                <th className="px-4 py-3 font-bold text-right">Radius</th>
                                                <th className="px-4 py-3 font-bold text-right">Satz</th>
                                                <th className="px-4 py-3 font-bold">Bewertung</th>
                                                <th className="px-4 py-3 font-bold">Status</th>
                                                <th className="px-4 py-3" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {gefiltert.map((f) => {
                                                const b = einsatzbereitschaft(f, qualisNachId.get(f.id) ?? [], heute);
                                                const ort = [f.zip, f.city].filter(Boolean).join(' ');
                                                return (
                                                    <tr
                                                        key={f.id}
                                                        onClick={() => setAusgewaehltId(f.id)}
                                                        className="border-b border-slate-50 last:border-0 hover:bg-blue-50/40 cursor-pointer"
                                                    >
                                                        <td className="px-4 py-3">
                                                            <span className="font-bold text-slate-800">{f.name}</span>
                                                            {f.firma && (
                                                                <span className="block text-[11px] text-slate-400">{f.firma}</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-600">
                                                            <span className="flex items-center gap-1.5">
                                                                {/* Ein Ort ohne Koordinaten taucht nicht auf der
                                                                    Karte auf und faellt aus dem Matching — das
                                                                    muss die Liste zeigen, nicht erst das Detail. */}
                                                                <MapPin
                                                                    className={`w-3.5 h-3.5 shrink-0 ${
                                                                        f.lat != null && f.lng != null ? 'text-emerald-500' : 'text-slate-300'
                                                                    }`}
                                                                />
                                                                <span className="truncate" title={formatAnschrift(f)}>
                                                                    {ort || '—'}
                                                                </span>
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                                                            {f.einsatzradiusKm} km
                                                        </td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                                                            {formatCents(f.satzJeBegehungCents) || '—'}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {f.rating ? (
                                                                <CustomerRatingStars value={f.rating} readOnly size="sm" />
                                                            ) : (
                                                                <span className="text-[11px] text-slate-300">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="flex flex-col gap-1 items-start">
                                                                <span
                                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${
                                                                        b.bereit
                                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                                            : 'bg-rose-50 text-rose-700 border-rose-200'
                                                                    }`}
                                                                    title={b.bereit ? undefined : b.gruende.join(' ')}
                                                                >
                                                                    {b.bereit ? (
                                                                        <CheckCircle2 className="w-3 h-3" />
                                                                    ) : (
                                                                        <AlertTriangle className="w-3 h-3" />
                                                                    )}
                                                                    {b.bereit ? 'Einsatzbereit' : 'Blockiert'}
                                                                </span>
                                                                {!b.bereit && (
                                                                    // Nur der ERSTE Grund in der Liste: die
                                                                    // vollstaendige Aufzaehlung steht im Detail
                                                                    // und im Tooltip, hier wuerde sie die Zeile
                                                                    // sprengen.
                                                                    <span className="text-[11px] text-slate-500 leading-tight">
                                                                        {b.gruende[0]}
                                                                        {b.gruende.length > 1 && ` (+${b.gruende.length - 1})`}
                                                                    </span>
                                                                )}
                                                                {b.bereit && f.status !== 'aktiv' && (
                                                                    <span className={`text-[11px] px-1.5 rounded border ${STATUS_KLASSEN[f.status]}`}>
                                                                        {STATUS_LABELS[f.status]}
                                                                    </span>
                                                                )}
                                                                {b.bereit && b.warnungen.length > 0 && (
                                                                    <span className="text-[11px] text-amber-600 leading-tight">
                                                                        {b.warnungen[0]}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <ChevronRight className="w-4 h-4 text-slate-300 inline-block" />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Neuanlage ── */}
            {anlegenOffen && (
                <div className="fixed inset-0 z-[100] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-slate-800">Neuer Freelancer</h3>
                            <button onClick={() => setAnlegenOffen(false)} className="p-1.5 text-slate-400 hover:text-slate-600">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1">Name</label>
                            <input
                                autoFocus
                                value={neuerName}
                                onChange={(e) => setNeuerName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') void anlegen(); }}
                                placeholder="Vor- und Nachname"
                                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                            />
                            <p className="text-[11px] text-slate-400 ml-1">
                                Adresse, Satz und Nachweise werden anschließend in der Akte gepflegt.
                            </p>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button variant="secondary" onClick={() => setAnlegenOffen(false)}>Abbrechen</Button>
                            <Button
                                variant="primary"
                                onClick={anlegen}
                                disabled={legtAn || !neuerName.trim()}
                                icon={legtAn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            >
                                Anlegen
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
